// searchMatch.js — punctuation-blind product search (Brian, 2026-08-24).
//
// Voice input says "firm tofu" but the product is "PMO) Premium Firm-Tofu
// (부침두부)12x454g" — hyphens, parentheses and word order broke the old
// plain-substring match. Here both sides are normalized (lowercase, all
// punctuation → spaces) and every word of the query must appear SOMEWHERE
// in the product's name/SKU/brand, in any order. "firm tofu", "tofu firm",
// and "Firm-Tofu" all match the same products, typed or spoken, EN or KO.
// Korean letters that SOUND alike get collapsed to one form on both sides —
// voice recognition spells by sound, product names by whoever typed them.
// Classes (field-driven + preemptive, 2026-08-24):
//   vowels:  ㅐ/ㅒ/ㅔ/ㅖ merge (배식해=배식혜); ㅙ/ㅚ/ㅞ merge; ㅢ→ㅣ
//   onsets:  tense → plain (ㄲ→ㄱ ㄸ→ㄷ ㅃ→ㅂ ㅆ→ㅅ ㅉ→ㅈ): 떡뽁이=떡복이
//   codas:   silent 사이시옷 ㅅ dropped (고춧가루=고추가루);
//            ㄲ/ㅋ→ㄱ, ㅈ/ㅊ/ㅌ/ㅆ→ㄷ, ㅍ→ㅂ (all sound identical word-final)
// Hangul syllable = 0xAC00 + (onset*21 + vowel)*28 + coda.
const VOWEL_MAP = { 1: 5, 3: 5, 7: 5, 10: 15, 11: 15, 19: 20 };
const ONSET_MAP = { 1: 0, 4: 3, 8: 7, 10: 9, 13: 12 };
const CODA_MAP = { 19: 0, 2: 1, 24: 1, 22: 7, 23: 7, 25: 7, 20: 7, 26: 17 };

function collapseKorean(s) {
  let out = "";
  for (const ch of s) {
    const code = ch.codePointAt(0);
    if (code >= 0xac00 && code <= 0xd7a3) {
      const idx = code - 0xac00;
      const onset = Math.floor(idx / 588);
      const vowel = Math.floor((idx % 588) / 28);
      const coda = idx % 28;
      const o = ONSET_MAP[onset] ?? onset;
      const v = VOWEL_MAP[vowel] ?? vowel;
      const c = CODA_MAP[coda] ?? coda;
      out += String.fromCharCode(0xac00 + (o * 21 + v) * 28 + c);
    } else {
      out += ch;
    }
  }
  return out;
}

export function normalizeSearch(s) {
  return collapseKorean(
    String(s ?? "")
      .toLowerCase()
      .replace(/[^a-z0-9가-힣ㄱ-ㆎ]+/gi, " ") // keep letters/digits/Korean
      .replace(/\s+/g, " ")
      .trim()
  );
}

/**
 * Does this product match the query? Two chances to hit:
 * 1. every query word appears somewhere (any order), OR
 * 2. the query with ALL spaces removed appears in the name with ALL spaces
 *    removed — so "통고추가루" finds "통 고추가루" and vice versa (spacing
 *    differs between spoken input and product names; Brian 2026-08-24).
 */
export function productMatches(p, query) {
  const q = normalizeSearch(query);
  if (!q) return true;
  const hay = normalizeSearch(`${p.name || ""} ${p.sku || ""} ${p.brand || ""}`);
  if (q.split(" ").every(tok => hay.includes(tok))) return true;
  return hay.replace(/ /g, "").includes(q.replace(/ /g, ""));
}

/**
 * RELAXED fallback (Brian, 2026-08-24): when the exact search finds NOTHING,
 * pages retry with this — any query word counts, and a long Korean compound
 * is split into its possible halves ("간장게장" → 간장 + 게장), so related
 * products surface instead of a dead end. Never used while the strict match
 * has results, so normal searches stay precise.
 */
function collapsedHay(p) {
  return normalizeSearch(`${p.name || ""} ${p.sku || ""} ${p.brand || ""}`).replace(/ /g, "");
}

export function productMatchesRelaxed(p, query) {
  const q = normalizeSearch(query);
  if (!q) return true;
  const hayC = collapsedHay(p);
  const parts = new Set();
  for (const tok of q.split(" ")) {
    if (tok.length >= 2) parts.add(tok);
    if (/^[가-힣]{4,}$/.test(tok)) {
      for (let i = 2; i <= tok.length - 2; i++) {
        parts.add(tok.slice(0, i));
        parts.add(tok.slice(i));
      }
    }
  }
  return [...parts].some(t => hayC.includes(t));
}

/**
 * Ranking for close-match results (Brian, 2026-08-24): what you MEANT floats
 * to the top. Whole query words score highest, split halves of a compound
 * less, and a mere shared beginning of an unmatched word (로즈 vs 로제 →
 * the 로) adds a small nudge — so "로즈 떡볶이" puts the 로제 떡볶이 above
 * every other 떡볶이. Higher = better; only used to SORT already-matched
 * results, never to admit new ones.
 */
export function relaxedScore(p, query) {
  const q = normalizeSearch(query);
  if (!q) return 0;
  const hayC = collapsedHay(p);
  let score = 0;
  let matchedAll = true;
  for (const tok of q.split(" ")) {
    if (hayC.includes(tok)) { score += tok.length * 10; continue; }
    let best = 0;
    if (/^[가-힣]{4,}$/.test(tok)) {
      for (let i = 2; i <= tok.length - 2; i++) {
        if (hayC.includes(tok.slice(0, i))) best = Math.max(best, i);
        if (hayC.includes(tok.slice(i))) best = Math.max(best, tok.length - i);
      }
    }
    if (best > 0) {
      score += best * 6;
    } else {
      // partial-beginning nudge: longest prefix of the word found anywhere
      for (let i = tok.length - 1; i >= 1; i--) {
        if (hayC.includes(tok.slice(0, i))) { score += i * 3; break; }
      }
      matchedAll = false;
    }
  }
  if (matchedAll) score += 50;
  return score;
}
